



































import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { TranslateResult } from "vue-i18n";
import {
  AvailabilityIntervalType,
  UserAvailabilityInterval,
} from "@scrile/api-provider/dist/projects/scrileConnect/UserAvailabilityProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import BaseButton from "@/components/BaseButton";
import BaseSegmentedButton from "@/components/BaseSegmentedButton";
import CustomScroll from "@/components/CustomScroll/CustomScroll.vue";
import AvailabilityRangeSettings from "./components/AvailabilityRangeSettings";
import { normalizeMinutes, shiftDayOfWeek, shiftMinutes } from "./tools";
import {
  DayOfWeek,
  DEFAULT_BEGIN_MINUTES,
  DEFAULT_DURATION_MINUTES,
  MINUTES_IN_DAY,
  MINUTES_IN_WEEK,
  UserAvailabilityDisplayInterval,
} from "./tools/types";

const timezoneOffset = new Date().getTimezoneOffset() * -1;
export const newIntervalDefaultValue: UserAvailabilityInterval = {
  beginMinutes: normalizeMinutes(DEFAULT_BEGIN_MINUTES - timezoneOffset, MINUTES_IN_WEEK),
  durationMinutes: DEFAULT_DURATION_MINUTES,
};

export default defineComponent({
  name: "AppointmentAvailabilitySettings",
  components: { BaseSegmentedButton, CustomScroll, AvailabilityRangeSettings, BaseButton },
  props: {
    manualSave: { type: Boolean },
    activeType: { type: String as PropType<AvailabilityIntervalType>, require: true },
  },
  setup(props, { emit }) {
    const { authUserStrict } = useAuthUser();
    const weekDaysSegments: { value: DayOfWeek; text: TranslateResult; title: TranslateResult }[] = [
      {
        value: DayOfWeek.Monday,
        text: $t("M"),
        title: $t("Monday"),
      },
      {
        value: DayOfWeek.Tuesday,
        text: $t("T"),
        title: $t("Tuesday"),
      },
      {
        value: DayOfWeek.Wednesday,
        text: $t("W"),
        title: $t("Wednesday"),
      },
      {
        value: DayOfWeek.Thursday,
        text: $t("T"),
        title: $t("Thursday"),
      },
      {
        value: DayOfWeek.Friday,
        text: $t("F"),
        title: $t("Friday"),
      },
      {
        value: DayOfWeek.Saturday,
        text: $t("S"),
        title: $t("Saturday"),
      },
      {
        value: DayOfWeek.Sunday,
        text: $t("S"),
        title: $t("Sunday"),
      },
    ];
    const activeDayNumber = ref(weekDaysSegments[0].value);
    const activeDay = computed(
      () => weekDaysSegments.find(d => d.value === activeDayNumber.value) ?? weekDaysSegments[0]
    );
    const intervals = ref<UserAvailabilityInterval[]>([]);
    const fetchIntervals = async () => {
      const response = await providers.UserAvailabilityProvider.search({
        data: { userIds: [authUserStrict.value.id] },
      });
      intervals.value = response.find(i => i.userId === authUserStrict.value.id)?.intervals ?? [];
      emit("setValid", !!intervals.value.length);
    };

    const updateIntervals = async (newIntervals: UserAvailabilityInterval[]) => {
      const response = await providers.UserAvailabilityProvider.update({
        data: {
          userId: authUserStrict.value.id,
          intervals: newIntervals,
        },
      });
      intervals.value = response.intervals;
    };

    const manualUpdateIntervals = (newIntervals: UserAvailabilityInterval[]) => {
      emit("setAvailability", newIntervals);
      emit("setValid", !!newIntervals.length);
    };

    const displayIntervals = computed<Record<DayOfWeek, UserAvailabilityDisplayInterval[]>>(() => {
      return intervals.value.reduce(
        (
          displayIntervals: Record<DayOfWeek, UserAvailabilityDisplayInterval[]>,
          intervalData: UserAvailabilityInterval,
          index: number
        ) => {
          const beginMinutes = normalizeMinutes(intervalData.beginMinutes + timezoneOffset, MINUTES_IN_WEEK);
          const endMinutes = normalizeMinutes(beginMinutes + intervalData.durationMinutes, MINUTES_IN_WEEK);
          const dayOfWeek: DayOfWeek = Math.floor(beginMinutes / MINUTES_IN_DAY);
          const displayInterval: UserAvailabilityDisplayInterval = {
            beginTime: {
              hours: ("0" + Math.floor((beginMinutes % MINUTES_IN_DAY) / 60)).slice(-2),
              minutes: ("0" + ((beginMinutes % MINUTES_IN_DAY) % 60)).slice(-2),
            },
            endTime: {
              hours: ("0" + Math.floor((endMinutes % MINUTES_IN_DAY) / 60)).slice(-2),
              minutes: ("0" + ((endMinutes % MINUTES_IN_DAY) % 60)).slice(-2),
            },
            dayOfWeek,
            index,
            type: intervalData.type,
          };

          displayIntervals[dayOfWeek] = displayIntervals[dayOfWeek]
            ? [...displayIntervals[dayOfWeek], displayInterval]
            : [displayInterval];
          return displayIntervals;
        },
        {} as Record<DayOfWeek, UserAvailabilityDisplayInterval[]>
      );
    });

    const activeDisplayIntervalsList = computed(
      () => displayIntervals.value[activeDay.value.value]?.filter(interval => interval.type === props.activeType) ?? []
    );

    const onAddInterval = () => {
      const newInterval = { ...newIntervalDefaultValue, type: props.activeType };
      if (activeDay.value.value !== DayOfWeek.Monday) {
        newInterval.beginMinutes = normalizeMinutes(
          newIntervalDefaultValue.beginMinutes + shiftDayOfWeek(DayOfWeek.Monday, activeDay.value.value),
          MINUTES_IN_WEEK
        );
      }
      intervals.value.push(newInterval);
      props.manualSave ? manualUpdateIntervals(intervals.value) : updateIntervals(intervals.value);
    };

    const onSetBeginTime = ({ interval, newTime }: { interval: UserAvailabilityDisplayInterval; newTime: string }) => {
      const { index, beginTime } = interval;
      const timeDifference = shiftMinutes(beginTime, newTime);
      intervals.value = intervals.value.map((v, i) => {
        if (i !== index) {
          return v;
        }
        return {
          ...v,
          beginMinutes: normalizeMinutes(intervals.value[index].beginMinutes + timeDifference, MINUTES_IN_WEEK),
          durationMinutes: normalizeMinutes(
            intervals.value[index].durationMinutes - timeDifference,
            MINUTES_IN_DAY,
            false
          ),
        };
      });
      props.manualSave ? manualUpdateIntervals(intervals.value) : updateIntervals(intervals.value);
    };

    const onSetEndTime = ({ interval, newTime }: { interval: UserAvailabilityDisplayInterval; newTime: string }) => {
      const { index, endTime } = interval;
      const timeDifference = shiftMinutes(endTime, newTime);
      intervals.value = intervals.value.map((v, i) => {
        if (i !== index) {
          return v;
        }
        return {
          ...v,
          durationMinutes: normalizeMinutes(
            intervals.value[index].durationMinutes + timeDifference,
            MINUTES_IN_DAY,
            false
          ),
        };
      });
      props.manualSave ? manualUpdateIntervals(intervals.value) : updateIntervals(intervals.value);
    };

    const onRemove = (interval: UserAvailabilityDisplayInterval) => {
      intervals.value.splice(interval.index, 1);
      props.manualSave ? manualUpdateIntervals(intervals.value) : updateIntervals(intervals.value);
    };

    const init = () => {
      emit("setCb", (newIntervals: UserAvailabilityInterval[]) => updateIntervals(newIntervals));
      emit("setValid", false);
      fetchIntervals();
    };
    init();

    return {
      intervals,
      activeDay,
      activeDayNumber,
      weekDaysSegments,
      activeDisplayIntervalsList,
      onAddInterval,
      onRemove,
      onSetEndTime,
      onSetBeginTime,
    };
  },
});
